export const getAudienceBadge = (highlights: string[] | undefined) => {
  if (!highlights || highlights.length === 0) return null;

  const highlight = highlights[0].toLowerCase();

  if (highlight.includes('high school') || highlight.includes('student')) {
    return {
      text: 'High School',
      className: 'bg-blue-600 text-white border-blue-700',
      icon: '🎓'
    };
  } else if (highlight.includes('university') || highlight.includes('college')) {
    return {
      text: 'University',
      className: 'bg-purple-600 text-white border-purple-700',
      icon: '🏛️'
    };
  } else if (highlight.includes('executive') || highlight.includes('professional') || highlight.includes('corporate')) {
    return {
      text: 'Executive',
      className: 'bg-gray-700 text-white border-gray-800',
      icon: '💼'
    };
  }

  // Default for any other audience
  return {
    text: `${highlights[0]}`,
    className: 'bg-blue-600 text-white border-blue-700',
    icon: '🎓'
  };
}

/**
 * Replaces "Harvard University" and "Ivy League University" with "Ivy League" in text content
 * This is used to maintain consistency across the application
 */
export const replaceHarvardWithIvyLeague = (text: string | undefined | null): string => {
  if (!text) return '';
  // First replace "Ivy League University" to avoid double replacement
  let result = text.replace(/Ivy League University/gi, 'Ivy League');
  // Then replace "Harvard University"
  result = result.replace(/Harvard University/gi, 'Ivy League');
  return result;
}

/**
 * Returns the currency symbol for a given currency code
 * Centralized currency symbol mapping used across the application
 * @param currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR', 'GBP')
 * @returns Currency symbol or the currency code if not found
 */
export const getCurrencySymbol = (currencyCode: string = 'USD'): string => {
  const symbols: { [key: string]: string } = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    AED: 'AED',
    SAR: 'SAR',
    KWD: 'KWD',
    QAR: 'QAR',
    BHD: 'BHD',
    OMR: 'OMR',
    CAD: 'CA$',
    AUD: 'A$',
    CHF: 'CHF',
    SGD: 'S$',
    JPY: '¥',
    CNY: '¥',
    INR: '₹',
    TRY: '₺',
    MYR: 'RM',
    EGP: 'E£',
  };
  return symbols[currencyCode] || '$';
};

/**
 * Returns the appropriate currency icon name for a given currency code
 * Used to display currency icons dynamically across the application
 * @param currencyCode - ISO 4217 currency code (e.g., 'USD', 'EUR', 'GBP')
 * @returns Icon name from lucide-react that corresponds to the currency
 */
export const getCurrencyIconName = (currencyCode: string = 'USD'): 
  'DollarSign' | 'Euro' | 'PoundSterling' | 'IndianRupee' | 'Coins' => {
  const iconMap: { [key: string]: 'DollarSign' | 'Euro' | 'PoundSterling' | 'IndianRupee' | 'Coins' } = {
    // Dollar-based currencies
    USD: 'DollarSign',
    CAD: 'DollarSign',
    AUD: 'DollarSign',
    SGD: 'DollarSign',
    
    // Euro
    EUR: 'Euro',
    
    // Pound Sterling
    GBP: 'PoundSterling',
    
    // Indian Rupee
    INR: 'IndianRupee',
    
    // Middle Eastern currencies - use Coins as generic
    AED: 'Coins',
    SAR: 'Coins',
    KWD: 'Coins',
    QAR: 'Coins',
    BHD: 'Coins',
    OMR: 'Coins',
    
    // Asian currencies
    JPY: 'Coins',
    CNY: 'Coins',
    MYR: 'Coins',
    
    // Turkish Lira
    TRY: 'Coins',
    
    // Egyptian Pound
    EGP: 'PoundSterling',
    
    // Swiss Franc
    CHF: 'Coins',
  };
  
  return iconMap[currencyCode] || 'DollarSign';
};

/**
 * Removes currency symbols from a formatted price string
 * Returns only the numeric value (e.g., "£3,499.00" → "3,499.00")
 * Handles all common currency symbols and formats
 * @param formattedPrice - Price string that may contain currency symbols
 * @returns Price string without currency symbols, trimmed
 */
export const removeCurrencySymbol = (formattedPrice: string): string => {
  if (!formattedPrice) return '';
  
  // Remove common currency symbols and codes
  // Matches: $, €, £, ¥, ₹, ₺, and text codes like USD, EUR, AED, SAR, etc.
  return formattedPrice
    .replace(/^[$€£¥₹₺CA$A$S$E£RM]+\s*/g, '') // Symbols at start with optional space
    .replace(/\s*[$€£¥₹₺CA$A$S$E£RM]+$/g, '') // Symbols at end with optional space
    .replace(/^(USD|EUR|GBP|AED|SAR|KWD|QAR|BHD|OMR|CAD|AUD|CHF|SGD|JPY|CNY|INR|TRY|MYR|EGP)\s*/gi, '') // Currency codes at start
    .replace(/\s*(USD|EUR|GBP|AED|SAR|KWD|QAR|BHD|OMR|CAD|AUD|CHF|SGD|JPY|CNY|INR|TRY|MYR|EGP)$/gi, '') // Currency codes at end
    .trim();
};